import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { angularPlugin } from '@datadog/browser-rum-angular';
import { environment } from '../../../environments/environment';
import { OWNING_TEAM, scrubEvent, scrubLog } from './datadog-redaction';

export type DatadogEnv = 'production' | 'preproduction' | 'test' | 'development';

export const OCC_HOST_ENV_RULES: ReadonlyArray<{ test: RegExp; env: DatadogEnv }> = [
  { test: /api\.\w+-(p\d+|prd)\b/, env: 'production' },
  // Custom-domain API hosts (e.g. https://api-p1.<domain>) have no `api.` prefix before the tier; anchored to the hostname so URLs that merely contain `api-p1.` elsewhere fall back to defaultEnv.
  { test: /^(?:https?:\/\/)?api-(p\d+|prd)\./, env: 'production' },
  { test: /^(?:https?:\/\/)?api-(s1|stg1)\./, env: 'preproduction' },
  { test: /-p\d+-public\./, env: 'production' },
  { test: /api\.\w+-(s1|stg1)\b/, env: 'preproduction' },
  { test: /-s1-public\./, env: 'preproduction' },
  { test: /api\.\w+-(s2|s3|qa1)\b/, env: 'test' },
  { test: /-(s2|s3)-public\./, env: 'test' },
  { test: /api\.\w+-d\d+\b/, env: 'development' },
  { test: /-d\d+-public\./, env: 'development' },
];

export function resolveDatadogEnv(occBaseUrl: string | null, defaultEnv: DatadogEnv): DatadogEnv {
  if (!occBaseUrl) {return defaultEnv;}
  for (const rule of OCC_HOST_ENV_RULES) {
    if (rule.test.test(occBaseUrl)) {return rule.env;}
  }
  return defaultEnv;
}

const OCC_HOST_META_PLACEHOLDER = 'OCC_BACKEND_BASE_URL_VALUE';

export function resolveOccBackendBaseUrl(): string | null {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="occ-backend-base-url"]');
  const injected = meta?.content?.trim();
  if (injected && injected !== OCC_HOST_META_PLACEHOLDER) {
    return injected;
  }
  return environment.occBaseUrl || null;
}

/**
 * OneTrust's Performance/Analytics cookie category for this tenant.
 * Confirmed by OneTrust support (USONESHOP-3916, ticket RITM27004509) to be
 * defined once at the tenant level, not per-site — the same value applies
 * to every OneTrust-gated storefront under this account, this one included.
 */
export const ONETRUST_PERFORMANCE_CATEGORY = 'C0002';

/**
 * Reads OneTrust's own consent state rather than trusting anything cached —
 * `OnetrustActiveGroups` is the data layer variable OneTrust itself
 * populates once a domain-ID match succeeds and the user has made (or
 * previously made) a choice. See USONESHOP-3916: this may be empty because
 * of a separate domain-ID mismatch bug also found on this storefront, so
 * this will correctly report "no consent" until that's fixed too.
 */
export function hasPerformanceConsent(activeGroups: string | null | undefined): boolean {
  return !!activeGroups?.split(',').includes(ONETRUST_PERFORMANCE_CATEGORY);
}

function currentTrackingConsent(): 'granted' | 'not-granted' {
  return hasPerformanceConsent((window as any).OnetrustActiveGroups) ? 'granted' : 'not-granted';
}

/**
 * Uses Datadog's Tracking Consent API, kept in sync with the user's actual
 * OneTrust Performance-cookie consent rather than unconditional on
 * bootstrap (USONESHOP-3916).
 *
 * The SDKs are always `init()`ed once — with `trackingConsent` reflecting
 * whatever OneTrust reports at that moment — because the Tracking Consent
 * API is designed for exactly this: with `not-granted`, the SDKs collect
 * nothing persisted/sent until consent flips to `granted` via
 * `setTrackingConsent()`. This also correctly handles a user *revoking*
 * consent mid-session: `OptanonWrapper` re-evaluates and calls
 * `setTrackingConsent()` on every OneTrust consent change, in both
 * directions, not just the first grant.
 */
@Injectable({ providedIn: 'root' })
export class DatadogRumService {
  constructor(@Inject(PLATFORM_ID) private platformId: object) {}

  init(): void {
    if (!isPlatformBrowser(this.platformId)) {return;}

    const cfg = environment.datadog;
    if (!cfg?.applicationId || !cfg?.clientToken) {return;}

    const occBaseUrl = resolveOccBackendBaseUrl();
    const env = resolveDatadogEnv(occBaseUrl, cfg.defaultEnv ?? 'development');

    datadogRum.init({
      applicationId: cfg.applicationId,
      clientToken: cfg.clientToken,
      site: cfg.site,
      service: cfg.service,
      env,
      trackingConsent: currentTrackingConsent(),
      sessionSampleRate: 100,
      // Session Replay is off: replay records bypass `beforeSend` and carry the full page URL, so a password-reset
      // link (?token= / ?code=) would reach Datadog in the replay metadata. Re-enable only once credential-bearing
      // URLs are excluded or sanitised before recording, including on client-side navigation.
      sessionReplaySampleRate: 0,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      // 'mask' hides rendered text as well as inputs: account, checkout and invoice pages show names, addresses and phone numbers.
      defaultPrivacyLevel: 'mask',
      // Automatic action names ignore defaultPrivacyLevel unless this is on; without it, clicking a customer email
      // link would send the address as action.target.name.
      enablePrivacyForActionName: true,
      // Drops events with unscrubbable credentials and redacts credentials in URLs and error text (reset / OTP / SSO links).
      beforeSend: scrubEvent,
      // Route patterns as view names instead of resolved URLs — requires
      // provideDatadogRouter() in main.ts's browser-only config.
      plugins: [angularPlugin({ router: true })],
    });

    datadogLogs.init({
      clientToken: cfg.clientToken,
      site: cfg.site,
      service: cfg.service,
      env,
      trackingConsent: currentTrackingConsent(),
      forwardErrorsToLogs: true,
      // Redacts credentials in log messages, error stacks and urls (reset / OTP / SSO links).
      beforeSend: scrubLog,
      sessionSampleRate: 100,
    });

    datadogRum.setGlobalContextProperty('region', 'eu');
    datadogRum.setGlobalContextProperty('subscription', 'sanofiave3');
    datadogRum.setGlobalContextProperty('site', cfg.siteName);
    datadogRum.setGlobalContextProperty('country', cfg.country);
    datadogRum.setGlobalContextProperty('architecture', 'ccv2');
    datadogRum.setGlobalContextProperty('codebase', cfg.service);
    datadogRum.setGlobalContextProperty('team', OWNING_TEAM);

    // RUM and Logs have separate global contexts — without this, Logs
    // events (including forwarded errors) wouldn't carry these tags at all.
    datadogLogs.setGlobalContextProperty('region', 'eu');
    datadogLogs.setGlobalContextProperty('subscription', 'sanofiave3');
    datadogLogs.setGlobalContextProperty('site', cfg.siteName);
    datadogLogs.setGlobalContextProperty('country', cfg.country);
    datadogLogs.setGlobalContextProperty('architecture', 'ccv2');
    datadogLogs.setGlobalContextProperty('codebase', cfg.service);
    datadogLogs.setGlobalContextProperty('team', OWNING_TEAM);

    const previousWrapper = (window as any).OptanonWrapper;
    (window as any).OptanonWrapper = () => {
      previousWrapper?.();
      const consent = currentTrackingConsent();
      datadogRum.setTrackingConsent(consent);
      datadogLogs.setTrackingConsent(consent);
    };
  }
}
